// src/admin/pages/Visitors.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EmailIcon from '@mui/icons-material/Email';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { adminVisitors, adminSendEmail } from '../../utils/api';

function toLocalDateLabel(iso) {
    const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return '—';
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
function renderTime(r) {
    const t = r.start_time || r.booking_time || r.time || '';
    if (!t) return '—';
    const hh = String(t).slice(0, 2),
        mm = String(t).slice(3, 5);
    const dt = new Date();
    dt.setHours(Number(hh) || 0, Number(mm) || 0, 0, 0);
    return dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
function titleKind(kind) {
    const k = String(kind || '').toLowerCase();
    if (k.startsWith('scho')) return 'School';
    if (k.startsWith('indi')) return 'Individual';
    if (k.startsWith('grou')) return 'Group';
    return k ? k[0].toUpperCase() + k.slice(1) : 'Individual';
}
function rGuessKind(r) {
    if (!r) return 'individual';
    const t = (r.type || '').toLowerCase();
    if (t) return t;
    if (r.school_name) return 'school';
    if (r.group_name || r.people > 1 || r.group_size > 1 || r.people_count > 1)
        return 'group';
    return 'individual';
}
function getName(r) {
    const kind = rGuessKind(r);
    if (kind === 'school')
        return (
            r.school_name ||
            r.group_name ||
            r.contact_name ||
            r.name ||
            '—'
        );
    if (kind === 'group')
        return r.group_name || r.contact_name || r.name || '—';
    return (
        r.name ||
        [r.first_name, r.last_name].filter(Boolean).join(' ') ||
        r.contact_name ||
        '—'
    );
}
const getPeople = (r) =>
    r.people ?? r.headcount ?? r.group_size ?? r.people_count ?? null;
const getDateISO = (r) =>
    r.booking_date || r.date || r.visit_date
        ? String(r.booking_date || r.date || r.visit_date).slice(0, 10)
        : null;
const getEmail = (r) => r.email || r.contact_email || '';
const getPhone = (r) => r.phone || r.contact_phone || '';
const getPaid = (r) =>
    typeof r.paid !== 'undefined'
        ? !!r.paid
        : String(
            r.payment_status || r.payment_type || r.payment_method || ''
        )
            .toLowerCase()
            .includes('paypal');

/** Helpers: strictly "label above value" */
const Field = ({ label, value }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="overline" sx={{ lineHeight: 1 }}>
            {label}
        </Typography>
        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {value ?? '—'}
        </Typography>
    </Box>
);
const FieldNote = ({ label, value }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="overline" sx={{ lineHeight: 1 }}>
            {label}
        </Typography>
        <Paper
            variant="outlined"
            sx={{ p: 1.5, whiteSpace: 'pre-wrap', minHeight: 120 }}
        >
            {value || '—'}
        </Paper>
    </Box>
);

function EmailDialog({ open, onClose, name, email, onSent }) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (open) {
            setSubject('');
            setMessage('');
        }
    }, [open, name, email]);

    const send = async () => {
        if (!email) return alert('No email address for this booking.');
        setSending(true);
        try {
            const res = await adminSendEmail({ to: email, subject, message });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok)
                throw new Error(data?.error || 'Failed to send message.');
            onSent?.();
            onClose?.();
        } catch (e) {
            alert(e.message || 'Failed to send message.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose?.();
            }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Send Message</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <Field label="Name" value={name || '—'} />
                <Field label="Email" value={email || '—'} />
                <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                />
                <TextField
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    multiline
                    minRows={8}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={send}
                    disabled={sending || !subject || !message}
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function BookingDetailDialog({ open, onClose, row, onLaunchMessage }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    if (!row) return null;

    const kind = rGuessKind(row);
    const dateISO = getDateISO(row);

    const fields = [
        { label: 'Name', value: getName(row) },
        { label: 'Type', value: titleKind(kind) },
        {
            label: 'Date',
            value: dateISO ? toLocalDateLabel(dateISO) : '—',
        },
        { label: 'Time', value: renderTime(row) },
        { label: 'People', value: getPeople(row) ?? '—' },
        { label: 'Email', value: getEmail(row) || '—' },
        { label: 'Phone', value: getPhone(row) || '—' },
        { label: 'Paid', value: getPaid(row) ? 'Yes' : 'No' },
    ];

    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose?.();
            }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { width: { xs: '100%', md: 900 } } }}
        >
            <DialogTitle>Booking Details</DialogTitle>
            <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* ONE LABEL PER ROW — strict single-column stack */}
                <Box sx={{ display: 'grid', rowGap: 1.25 }}>
                    {fields.map((f) => (
                        <Field key={f.label} label={f.label} value={f.value} />
                    ))}

                    {kind === 'school' && (
                        <>
                            <Field
                                label="School"
                                value={row.school_name || '—'}
                            />
                            <Field label="Grade" value={row.grade || '—'} />
                            <Field
                                label="Teacher"
                                value={row.teacher_name || '—'}
                            />
                        </>
                    )}
                    {kind === 'group' && (
                        <>
                            <Field
                                label="Group"
                                value={row.group_name || '—'}
                            />
                            <Field
                                label="Organization"
                                value={row.organization || '—'}
                            />
                        </>
                    )}

                    <FieldNote
                        label="Notes"
                        value={row.comments || row.notes || '—'}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button startIcon={<EmailIcon />} onClick={onLaunchMessage}>
                    Send Message
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function VisitorsPage() {
    const [tab, setTab] = useState('upcoming');
    const [rows, setRows] = useState([]);

    // Single date selector (UI). We'll pass it as start=end to the API for compatibility.
    const [date, setDate] = useState('');

    const [loading, setLoading] = useState(false);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRow, setDetailRow] = useState(null);
    const [emailDlg, setEmailDlg] = useState({
        open: false,
        name: '',
        email: '',
    });
    const [snack, setSnack] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const load = async (opts) => {
        setLoading(true);
        const data = await adminVisitors(opts);
        setRows(data.rows || []);
        setLoading(false);
    };
    useEffect(() => {
        load({ tab: 'upcoming' });
    }, []);

    const onSearch = () => {
        const opts = { tab };
        if (date) {
            opts.start = date;
            opts.end = date;
        }
        load(opts);
    };

    const groups = useMemo(() => {
        const g = new Map();
        for (const r of rows) {
            const iso = getDateISO(r) || 'Unknown';
            if (!g.has(iso)) g.set(iso, []);
            g.get(iso).push(r);
        }
        for (const [, arr] of g) {
            arr.sort((a, b) => {
                const ta = (a.start_time || a.booking_time || '').slice(0, 5);
                const tb = (b.start_time || b.booking_time || '').slice(0, 5);
                return tab === 'previous'
                    ? tb.localeCompare(ta)
                    : ta.localeCompare(tb);
            });
        }
        return Array.from(g.entries()).sort(([a], [b]) =>
            tab === 'previous' ? (a < b ? 1 : -1) : (a > b ? 1 : -1)
        );
    }, [rows, tab]);

    /* ---------------- PDF Download (no Paid / Notes columns) ---------------- */
    const handleDownloadPdf = () => {
        // Flatten grouped rows into a single list while keeping the date label
        const flat = [];
        groups.forEach(([dateISO, list]) => {
            const dateLabel =
                dateISO && dateISO !== 'Unknown'
                    ? toLocalDateLabel(dateISO)
                    : 'Unknown date';
            list.forEach((r) => flat.push({ dateLabel, row: r }));
        });

        if (!flat.length) {
            alert('No visitors to include in the report.');
            return;
        }

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'pt',
            format: 'a4',
        });

        const isPrevious = tab === 'previous';
        const title = isPrevious
            ? 'Previous Visits Report'
            : 'Upcoming Bookings Report';
        const generatedAt = new Date().toLocaleString();
        const dateFilterLabel = date
            ? toLocalDateLabel(date)
            : 'All dates';

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text(title, 40, 40);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(80);
        doc.text(`Generated: ${generatedAt}`, 40, 58);
        doc.text(
            `View: ${isPrevious ? 'Previous Visits' : 'Upcoming Bookings'}`,
            40,
            74
        );
        doc.text(`Date filter: ${dateFilterLabel}`, 40, 90);
        doc.text(`Total bookings: ${flat.length}`, 40, 106);

        // NOTE: Paid + Notes intentionally omitted
        const head = [
            ['Date', 'Name', 'Type', 'Time', 'People', 'Email', 'Phone'],
        ];

        const body = flat.map(({ dateLabel, row }) => {
            const people = getPeople(row);
            return [
                dateLabel,
                getName(row) || '—',
                titleKind(rGuessKind(row)) || '—',
                renderTime(row) || '—',
                people === null || people === undefined || people === ''
                    ? '—'
                    : String(people),
                getEmail(row) || '—',
                getPhone(row) || '—',
            ];
        });

        autoTable(doc, {
            head,
            body,
            startY: 130,
            styles: {
                fontSize: 9,
                cellPadding: 6,
                valign: 'middle',
                overflow: 'linebreak',
            },
            headStyles: {
                fillColor: [33, 150, 243], // soft blue
                textColor: 255,
                fontSize: 10,
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245], // light gray striping
            },
            bodyStyles: {
                textColor: 40,
            },
            margin: { top: 120, left: 40, right: 40, bottom: 40 },
        });

        const baseName = isPrevious
            ? 'previous-visits'
            : 'upcoming-bookings';
        const fileName = `${baseName}-${new Date()
            .toISOString()
            .slice(0, 10)}.pdf`;

        doc.save(fileName);
    };

    return (
        <Box>
            <Paper elevation={1} sx={{ position: 'sticky', top: 0, zIndex: 5, p: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Bookings
                </Typography>

                <Tabs
                    value={tab}
                    onChange={(_e, v) => {
                        setTab(v);
                        load({
                            tab: v,
                            ...(date ? { start: date, end: date } : {}),
                        });
                    }}
                    sx={{ mb: 1 }}
                >
                    <Tab value="upcoming" label="Upcoming Bookings" />
                    <Tab value="previous" label="Previous Visits" />
                </Tabs>

                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        <Typography variant="body2">Date:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            size="small"
                            label="Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onSearch}
                        >
                            Search
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadPdf}
                            disabled={loading || !groups.length}
                        >
                            Download PDF
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Tip: All dates are searched by default.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Mobile: card layout grouped by day */}
            {isMobile ? (
                <Box sx={{ p: 1 }}>
                    {loading && (
                        <Typography align="center" sx={{ my: 2 }}>
                            Loading…
                        </Typography>
                    )}
                    {!loading && groups.length === 0 && (
                        <Typography align="center" sx={{ my: 2 }}>
                            No bookings found.
                        </Typography>
                    )}
                    {!loading &&
                        groups.map(([dateISO, list]) => (
                            <Box key={dateISO} sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        bgcolor: 'action.selected',
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {dateISO !== 'Unknown'
                                            ? toLocalDateLabel(dateISO)
                                            : 'Unknown date'}
                                    </Typography>
                                </Box>

                                {list.map((r, i) => (
                                    <Paper
                                        key={r.id ?? `${dateISO}-${i}`}
                                        variant="outlined"
                                        sx={{
                                            p: 1.25,
                                            mt: 1,
                                            borderRadius: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => {
                                                    setDetailRow(r);
                                                    setDetailOpen(true);
                                                }}
                                            >
                                                {getName(r)}
                                            </Button>
                                            <Chip
                                                size="small"
                                                label={titleKind(rGuessKind(r))}
                                            />
                                        </Box>

                                        <Grid
                                            container
                                            spacing={1}
                                            sx={{ mt: 0.5 }}
                                        >
                                            <Grid item xs={6}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Time
                                                </Typography>
                                                <Typography variant="body2">
                                                    {renderTime(r)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    People
                                                </Typography>
                                                <Typography variant="body2">
                                                    {getPeople(r) ?? '—'}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Email
                                                </Typography>
                                                {getEmail(r) ? (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        startIcon={<EmailIcon />}
                                                        onClick={() =>
                                                            setEmailDlg({
                                                                open: true,
                                                                name: getName(r),
                                                                email: getEmail(r),
                                                            })
                                                        }
                                                        sx={{
                                                            p: 0,
                                                            minWidth: 0,
                                                            wordBreak:
                                                                'break-all',
                                                        }}
                                                    >
                                                        {getEmail(r)}
                                                    </Button>
                                                ) : (
                                                    <Typography variant="body2">
                                                        —
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Phone
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {getPhone(r) || '—'}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Paid
                                                </Typography>
                                                <Typography variant="body2">
                                                    {getPaid(r) ? 'Yes' : 'No'}
                                                </Typography>
                                            </Grid>

                                            {(r.comments || r.notes) && (
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Notes
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            whiteSpace:
                                                                'pre-wrap',
                                                        }}
                                                    >
                                                        {r.comments ||
                                                            r.notes}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Paper>
                                ))}
                            </Box>
                        ))}
                </Box>
            ) : (
                // Desktop: table view
                <TableContainer
                    component={Paper}
                    sx={{
                        maxHeight: {
                            xs: '60dvh',
                            md: 'calc(100dvh - 280px)',
                        },
                        overflowX: 'auto',
                        '& td, & th': {
                            wordBreak: 'break-word',
                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                        },
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell>People</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Paid</TableCell>
                                <TableCell>Notes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && groups.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No bookings found.
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading &&
                                groups.map(([dateISO, list]) => (
                                    <React.Fragment key={dateISO}>
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                sx={{
                                                    bgcolor: 'action.selected',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {dateISO !== 'Unknown'
                                                    ? toLocalDateLabel(dateISO)
                                                    : 'Unknown date'}
                                            </TableCell>
                                        </TableRow>
                                        {list.map((r, i) => (
                                            <TableRow
                                                key={
                                                    r.id ?? `${dateISO}-${i}`
                                                }
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor:
                                                            'action.hover',
                                                    },
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'primary.main',
                                                        '& *': {
                                                            color: 'white',
                                                        },
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        onClick={() => {
                                                            setDetailRow(r);
                                                            setDetailOpen(true);
                                                        }}
                                                    >
                                                        {getName(r)}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={titleKind(
                                                            rGuessKind(r)
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {renderTime(r)}
                                                </TableCell>
                                                <TableCell>
                                                    {getPeople(r) ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {getEmail(r) ? (
                                                        <Button
                                                            variant="text"
                                                            size="small"
                                                            startIcon={
                                                                <EmailIcon />
                                                            }
                                                            onClick={() =>
                                                                setEmailDlg({
                                                                    open: true,
                                                                    name: getName(
                                                                        r
                                                                    ),
                                                                    email: getEmail(
                                                                        r
                                                                    ),
                                                                })
                                                            }
                                                        >
                                                            {getEmail(r)}
                                                        </Button>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {getPhone(r) || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {getPaid(r) ? (
                                                        <CheckIcon aria-label="paid" />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {r.comments ||
                                                        r.notes ||
                                                        ''}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <BookingDetailDialog
                open={detailOpen}
                row={detailRow}
                onClose={() => setDetailOpen(false)}
                onLaunchMessage={() => {
                    const r = detailRow || {};
                    setEmailDlg({
                        open: true,
                        name: getName(r),
                        email: getEmail(r),
                    });
                }}
            />
            <EmailDialog
                open={emailDlg.open}
                name={emailDlg.name}
                email={emailDlg.email}
                onClose={() =>
                    setEmailDlg({ open: false, name: '', email: '' })
                }
                onSent={() => setSnack(true)}
            />

            <Snackbar
                open={snack}
                autoHideDuration={3000}
                onClose={() => setSnack(false)}
            >
                <Alert
                    severity="success"
                    onClose={() => setSnack(false)}
                >
                    Message sent.
                </Alert>
            </Snackbar>
        </Box>
    );
}
